import { Head, Link, router } from "@inertiajs/react";
import {
  Badge,
  Button,
  Callout,
  Card,
  Flex,
  Select,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useMemo, useState } from "react";

import { AppLayout } from "@/components/app-layout";
import type { SharedProps } from "@/lib/types";

type ProjectGroupRow = {
  id: number;
  name: string;
  group_type: "regular" | "roll_up";
  default_order: number;
  roll_up_target_group_id: number | null;
  roll_up_target_group_name: string | null;
  member_count: number;
  member_project_names: string[];
};

type ProjectGroupsIndexProps = SharedProps & {
  projectGroups: ProjectGroupRow[];
  regularGroupOptions: Array<{ id: number; name: string }>;
};

type EditableGroup = {
  name: string;
  group_type: "regular" | "roll_up";
  default_order: number;
  roll_up_target_group_id: number | null;
};

export default function ProjectGroupsIndex({
  auth,
  flash,
  projectGroups,
  regularGroupOptions,
}: ProjectGroupsIndexProps) {
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"regular" | "roll_up">("regular");
  const [newDefaultOrder, setNewDefaultOrder] = useState("0");
  const [editing, setEditing] = useState<Record<number, EditableGroup>>({});
  const defaultRegularGroupId = useMemo(() => {
    const unassigned = regularGroupOptions.find((group) => group.name === "Unassigned");
    return unassigned?.id ?? regularGroupOptions[0]?.id ?? null;
  }, [regularGroupOptions]);

  const filteredGroups = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return projectGroups;
    return projectGroups.filter((group) => group.name.toLowerCase().includes(normalized));
  }, [projectGroups, search]);

  function createGroup() {
    router.post(
      "/settings/project_groups",
      {
        project_group: {
          name: newName,
          group_type: newType,
          default_order: normalizeOrderValue(newDefaultOrder),
          roll_up_target_group_id: newType === "roll_up" ? defaultRegularGroupId : null,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setNewName("");
          setNewType("regular");
          setNewDefaultOrder("0");
        },
      },
    );
  }

  function startEdit(group: ProjectGroupRow) {
    setEditing((current) => ({
      ...current,
      [group.id]: {
        name: group.name,
        group_type: group.group_type,
        default_order: group.default_order,
        roll_up_target_group_id: group.roll_up_target_group_id,
      },
    }));
  }

  function saveEdit(groupId: number) {
    const current = editing[groupId];
    if (!current) return;

    router.patch(
      `/settings/project_groups/${groupId}`,
      {
        project_group: current,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEditing((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
        },
      },
    );
  }

  function removeGroup(groupId: number) {
    router.delete(`/settings/project_groups/${groupId}`, { preserveScroll: true });
  }

  function setEditValue(groupId: number, key: "name" | "group_type", value: string) {
    setEditing((current) => ({
      ...current,
      [groupId]: {
        ...(current[groupId] || { name: "", group_type: "regular", default_order: 0, roll_up_target_group_id: null }),
        [key]: value,
        roll_up_target_group_id:
          key === "group_type" && value === "regular"
            ? null
            : (current[groupId]?.roll_up_target_group_id ?? defaultRegularGroupId),
      } as EditableGroup,
    }));
  }

  function setEditDefaultOrder(groupId: number, value: string) {
    setEditing((current) => ({
      ...current,
      [groupId]: {
        ...(current[groupId] || { name: "", group_type: "regular", default_order: 0, roll_up_target_group_id: null }),
        default_order: normalizeOrderValue(value),
      },
    }));
  }

  function updateRollUpTarget(group: ProjectGroupRow, targetIdValue: string) {
    const targetId = Number(targetIdValue);
    if (!Number.isFinite(targetId)) return;

    router.patch(
      `/settings/project_groups/${group.id}`,
      {
        project_group: {
          roll_up_target_group_id: targetId,
        },
      },
      {
        preserveScroll: true,
        preserveState: true,
      },
    );
  }

  return (
    <>
      <Head title="Project Groups" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="Project Groups"
        contextControls={
          <Flex gap="2" align="end">
            <div>
              <Text as="label" size="2" weight="medium">
                Search groups
              </Text>
              <TextField.Root
                mt="1"
                placeholder="Filter by name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </Flex>
        }
        pageActions={
          <Flex gap="2">
            <Link href="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </Flex>
        }
      >
        <div className="space-y-4">
          {flash.notice && (
            <Callout.Root color="green" variant="soft">
              <Callout.Text>{flash.notice}</Callout.Text>
            </Callout.Root>
          )}
          {flash.alert && (
            <Callout.Root color="red" variant="soft">
              <Callout.Text>{flash.alert}</Callout.Text>
            </Callout.Root>
          )}

          <Card className="p-4">
            <Flex gap="2" wrap="wrap" align="end">
              <div>
                <Text as="label" size="2" weight="medium">
                  Name
                </Text>
                <TextField.Root
                  mt="1"
                  placeholder="Project group name"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                />
              </div>
              <div>
                <Text as="label" size="2" weight="medium">
                  Type
                </Text>
                <Select.Root
                  value={newType}
                  onValueChange={(value) => setNewType(value as "regular" | "roll_up")}
                >
                  <Select.Trigger mt="1" />
                  <Select.Content>
                    <Select.Item value="regular">Regular</Select.Item>
                    <Select.Item value="roll_up">Roll-up</Select.Item>
                  </Select.Content>
                </Select.Root>
              </div>
              <div>
                <Text as="label" size="2" weight="medium" htmlFor="new-group-default-order">
                  Default Order
                </Text>
                <TextField.Root
                  id="new-group-default-order"
                  mt="1"
                  type="number"
                  min={0}
                  value={newDefaultOrder}
                  onChange={(event) => setNewDefaultOrder(event.target.value)}
                />
              </div>
              <Button onClick={createGroup} disabled={!newName.trim()}>
                Create Group
              </Button>
            </Flex>
          </Card>

          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell className="w-[24%]">Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[24%]">Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[14%]">Default Order</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[14%]">Members</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="w-[24%] whitespace-normal">
                  Actions
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredGroups.map((group) => {
                const editValue = editing[group.id];
                const isUnassigned = group.name === "Unassigned";
                const rowClassName = group.group_type === "regular" ? "bg-gray-50" : "";
                const typeValue = (editValue?.group_type ?? group.group_type) as
                  | "regular"
                  | "roll_up";
                const rollUpTargetId =
                  editValue?.roll_up_target_group_id ??
                  group.roll_up_target_group_id ??
                  defaultRegularGroupId;

                return (
                  <Table.Row
                    key={group.id}
                    className={rowClassName}
                    data-testid={`project-group-row-${group.id}`}
                  >
                    <Table.Cell className={rowClassName}>
                      {editValue ? (
                        <TextField.Root
                          value={editValue.name}
                          onChange={(event) => setEditValue(group.id, "name", event.target.value)}
                        />
                      ) : (
                        <Text weight="medium">{group.name}</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell className={rowClassName}>
                      {editValue ? (
                        <Flex direction="column" gap="2">
                          <Select.Root
                            value={editValue.group_type}
                            onValueChange={(value) => setEditValue(group.id, "group_type", value)}
                          >
                            <Select.Trigger />
                            <Select.Content>
                              <Select.Item value="regular">Regular</Select.Item>
                              <Select.Item value="roll_up">Roll-up</Select.Item>
                            </Select.Content>
                          </Select.Root>
                          {typeValue === "roll_up" && (
                            <div>
                              <Text as="div" size="1" color="gray">
                                Use group
                              </Text>
                              <Select.Root
                                value={rollUpTargetId ? String(rollUpTargetId) : ""}
                                onValueChange={(value) => {
                                  setEditing((current) => ({
                                    ...current,
                                    [group.id]: {
                                      ...(current[group.id] || {
                                        name: group.name,
                                        group_type: "roll_up",
                                        default_order: group.default_order,
                                        roll_up_target_group_id: null,
                                      }),
                                      roll_up_target_group_id: Number(value),
                                    },
                                  }));
                                }}
                              >
                                <Select.Trigger mt="1" />
                                <Select.Content>
                                  {regularGroupOptions.map((option) => (
                                    <Select.Item key={option.id} value={String(option.id)}>
                                      {option.name}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            </div>
                          )}
                        </Flex>
                      ) : (
                        <Flex direction="column" gap="2">
                          <Badge color={group.group_type === "roll_up" ? "purple" : "gray"}>
                            {group.group_type === "roll_up" ? "Roll-up" : "Regular"}
                          </Badge>
                          {group.group_type === "roll_up" && (
                            <div>
                              <Text as="div" size="1" color="gray">
                                Use group
                              </Text>
                              <Select.Root
                                value={rollUpTargetId ? String(rollUpTargetId) : ""}
                                onValueChange={(value) => updateRollUpTarget(group, value)}
                              >
                                <Select.Trigger mt="1" />
                                <Select.Content>
                                  {regularGroupOptions.map((option) => (
                                    <Select.Item key={option.id} value={String(option.id)}>
                                      {option.name}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            </div>
                          )}
                        </Flex>
                      )}
                    </Table.Cell>
                    <Table.Cell className={rowClassName}>
                      {editValue ? (
                        <div>
                          <Text as="label" size="1" color="gray" htmlFor={`group-default-order-${group.id}`}>Default Order</Text>
                          <TextField.Root
                            id={`group-default-order-${group.id}`}
                            mt="1"
                            type="number"
                            min={0}
                            value={String(editValue.default_order)}
                            onChange={(event) => setEditDefaultOrder(group.id, event.target.value)}
                          />
                        </div>
                      ) : (
                        <Text>{group.default_order}</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell className={rowClassName}>
                      <Text>{group.member_count}</Text>
                      {!!group.member_project_names.length && (
                        <Text
                          as="div"
                          size="1"
                          color="gray"
                          className="whitespace-normal break-words"
                        >
                          {group.member_project_names.join(", ")}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell className={`${rowClassName} whitespace-normal break-words`}>
                      <Flex gap="2" wrap="wrap">
                        {editValue ? (
                          <>
                            <Button size="1" onClick={() => saveEdit(group.id)}>
                              Save
                            </Button>
                            <Button
                              size="1"
                              variant="soft"
                              onClick={() =>
                                setEditing((current) => {
                                  const next = { ...current };
                                  delete next[group.id];
                                  return next;
                                })
                              }
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => startEdit(group)}
                              disabled={isUnassigned}
                            >
                              Edit {group.name}
                            </Button>
                            <Button
                              size="1"
                              color="red"
                              onClick={() => removeGroup(group.id)}
                              disabled={isUnassigned}
                            >
                              Delete {group.name}
                            </Button>
                          </>
                        )}
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </div>
      </AppLayout>
    </>
  );
}

function normalizeOrderValue(rawValue: string): number {
  const parsed = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}
